import { reportsTableRetrieveOptions } from '@crm/api'
import { AppLink } from '@crm/components'
import { ReportSelect } from '@crm/features/reports/components'
import { usePaginationConfigCrm } from '@crm/hooks'
import { capitalize } from '@crm/utils'
import { useLocales } from '@shared/locales'
import { DATE_TIME_FORMAT, displayDate, format } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Flex, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'

const { Title } = Typography

type Props = {
  id?: string
}

const KEYS_TO_SKIP = ['_id', 'custom_field_values']

export const DynamicTable = ({ id }: Props) => {
  const { t } = useLocales()

  const getFilterType = (values: dayjs.ConfigType) => {
    return {
      DateTime: format(values, DATE_TIME_FORMAT),
      Date: displayDate(values),
      Link: <AppLink to={`/leads/${values}`}>{t('buttons.open_link')}</AppLink>,
      default: typeof values === 'number' ? values : values || '-'
    }
  }

  const { data, isLoading } = useQuery({
    ...reportsTableRetrieveOptions({
      path: {
        id: Number(id)
      }
    })
  })

  const columns =
    data?.headers && data?.headers?.length > 0
      ? data?.headers?.map(({ field_name, label }) => ({
          title: label,
          key: field_name,
          dataIndex: field_name
        }))
      : Object.keys(data?.results?.[0] || {})
          .filter((key) => !KEYS_TO_SKIP.some((skip) => key.includes(skip)))
          .map((item) => ({
            title: capitalize(item.split('_').join(' ')),
            key: item,
            dataIndex: item,
            noWrap: true,
            render: (value: dayjs.ConfigType) => {
              if (dayjs(value, DATE_TIME_FORMAT, false).isValid()) {
                return getFilterType(value)
              }
              return value
            }
          }))

  return (
    <Flex vertical gap={16}>
      <div>
        <ReportSelect />
      </div>

      {data?.totals?.map((total) => (
        <Tag key={total.value}>
          <Title level={5}>{total?.value}</Title>
          <>
            {t('reports.total')} {total?.label}
          </>
        </Tag>
      ))}

      <Table
        key={uuidv4()}
        loading={isLoading}
        scroll={{ x: 1200 }}
        dataSource={data?.results}
        bordered
        columns={columns}
        pagination={usePaginationConfigCrm(data)}
        showSorterTooltip={false}
      />
    </Flex>
  )
}
